import React, { FC, useEffect, useState, Suspense, useDeferredValue } from 'react';
import {
  Avatar,
  Box,
  ButtonAppearance,
  Checkbox,
  Combobox,
  Icon,
  IconSize,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  Text,
  TextFieldSize,
  Spinner,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  CrewMemberList,
  PersonaType,
  Weld,
  AssignCommoditiesInput,
  AssignCommodityInput,
  CommodityAssignmentError,
  CommodityStatusCode,
  CommodityAssignee,
  CrewMember,
} from '@bechtel/types';
import { Form, Formik, Field, useField, FormikProps } from 'formik';
import { t, Trans } from '@lingui/macro';
import {
  DrawerFooterButtonStyled,
  ErrorRetry,
  TextWithEllipsis,
  useName,
  ErrorBoundary,
  SuspenseLoader,
  useWeldAssignees,
  useAppUser,
  WeldMilestoneType,
} from '@bechtel/client-common';
import { find, get } from 'lodash';
import { NetworkStatus, ApolloError } from '@apollo/client';
import { BlockHeadingStyled, CrewIconStyled, SideSheetSubHeaderStyled } from './styled';
import { EllipseStyled } from '~/components/molecules/weld-sub-header';
import { AssignCommodityCrewMembersErrorModal } from '~/components/molecules/assign-error-modal';
import { useAssignCommodityCrewMembers, useCrewMember, useWeldPermissions } from '~/hooks';
import { FormikState } from './types';

export interface AssignWeldOwnerProps {
  weld: Weld;
  onClose: () => void;
  openFromWeldModule?: boolean;
}

const initialValues: FormikState = {
  foreman: null,
  fieldEngineer: null,
  isAutoReassign: false,
};

export const CrewMemberTemplate = ({ item }) => {
  const { getFullName } = useName();
  const userName = !item?.firstName ? t`Unassigned` : getFullName(item.firstName, item.lastName);
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        {item?.imageUrl ? (
          <Avatar data-testid="avatar-regular-owner" src={item.imageUrl} />
        ) : (
          <CrewIconStyled>
            <Icon fillColor="black" size={IconSize.LARGE} icon="user-hard-hat" />
          </CrewIconStyled>
        )}
      </Box>
      <Text size="400" family="graphik" color="grey.1000">
        {userName}
      </Text>
    </Box>
  );
};

const WeldPersonaSelector = ({ name, items, placeHolderLabel, loading, onSearch }) => {
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  const { getFullName } = useName();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDeferredValue(searchText);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  return (
    <Box
      flexDirection={{ sm: 'column' }}
      px={{ sm: '450' }}
      py={{ sm: '500' }}
      css={{ flexGrow: 1 }}
      data-testid={`weld-${name}`}
    >
      <Combobox
        size={TextFieldSize.LARGE}
        items={items}
        itemComponent={CrewMemberTemplate}
        selectedComponent={CrewMemberTemplate}
        term={searchText}
        setTerm={setSearchText}
        placeholder={placeHolderLabel}
        itemString={itemToString}
        loading={loading}
        selectedItem={field && field.value ? field.value : t`Unassigned`}
        restorePreviousOnSkip
        onSelect={(item: CrewMember) => {
          setValue(item);
        }}
        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setSearchText(event.target.value);
          if (debouncedSearchText && debouncedSearchText.length >= 3) {
            onSearch(debouncedSearchText);
          }
        }}
      />
    </Box>
  );
};

const ForemanList = () => {
  const [
    getForemanList,
    {
      data: foremanData,
      error: foremanError,
      loading: foremanLoading,
      refetch: foremanRefetch,
      networkStatus: foremanNetworkStatus,
    },
  ] = useCrewMember();
  const foremanList = get(foremanData, 'crewMembers.items', []) as CrewMemberList['items'];
  useEffect(() => {
    getForemanList({
      variables: {
        input: {
          persona: PersonaType.FOREMAN,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [getForemanList]);
  const onSearchForeman = (searchText: string) => {
    if (searchText.length > 0 && searchText.length < 3) return;
    getForemanList({
      variables: {
        input: {
          searchKeyword: searchText,
          persona: PersonaType.FOREMAN,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  };
  return (
    <>
      <BlockHeadingStyled>
        <Trans>Select Foreman</Trans>
      </BlockHeadingStyled>
      {!foremanLoading && foremanError && (
        <div data-testid="welder-error">
          <ErrorRetry refetch={foremanRefetch}>
            <Trans>There was a problem loading Stencils</Trans>
          </ErrorRetry>
        </div>
      )}
      {!foremanError && (
        <WeldPersonaSelector
          name="foreman"
          items={foremanList}
          placeHolderLabel={t`Search Weld Foreman`}
          loading={foremanLoading || foremanNetworkStatus === NetworkStatus.refetch}
          onSearch={onSearchForeman}
        />
      )}
      <BlockHeadingStyled>
        <Trans>Temporary Assignment</Trans>
      </BlockHeadingStyled>
      <Field name="isAutoReassign">
        {({ field }) => {
          return (
            <Box
              px={{ sm: '450' }}
              py={{ sm: '500' }}
              data-testid="weld-isautoreassign"
              css={'opacity:0.5; pointer-events:none'}
            >
              <Box mr={{ sm: '500' }}>
                <Checkbox {...field} />
              </Box>
              <Text color="grey.1000" size="400" family="graphikCompact">
                <Trans>Automatically Reassign to Me </Trans>
                <Text color="grey.600">
                  &nbsp;<Trans>(End of the next shift)</Trans>
                </Text>
              </Text>
            </Box>
          );
        }}
      </Field>
    </>
  );
};

export const AssignWeldOwner: FC<AssignWeldOwnerProps> = ({ onClose, weld, openFromWeldModule }) => {
  const { user } = useAppUser();
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const dispatchToast = useToastsDispatch();
  const { commodityAssignees, currentMilestoneType, fitupRequest } = weld ? weld : {};
  const isFitupMilestone = currentMilestoneType?.name?.toUpperCase() == WeldMilestoneType.FITUP;
  const isWeldingRequired = fitupRequest && fitupRequest.isWeldingRequired ? fitupRequest.isWeldingRequired : false;
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(commodityAssignees);
  const { canAssignFieldWeldEngineer, canAssignWeldForeman, canReassignWeldForeman } = useWeldPermissions(
    assignedCrewMembers
  );
  const canAssignForeman =
    (isFitupMilestone ? isWeldingRequired : !isFitupMilestone) && (canAssignWeldForeman || canReassignWeldForeman);

  const [assignCommodityCrewMembersErrors, setAssignCommodityCrewMembersErrors] = useState<
    CommodityAssignmentError[] | ApolloError | null
  >();
  useEffect(() => {
    if (!commodityAssignees) return;
    const weldFieldEngineer = find(
      commodityAssignees,
      (weldAssignee) => weldAssignee.personaType === PersonaType.FIELD_WELD_ENGINEER
    );
    const weldForeman = find(commodityAssignees, (weldAssignee) => {
      return weldAssignee.personaType === PersonaType.FOREMAN;
    });
    initialValues.foreman = {
      __typename: 'CrewMember',
      id: weldForeman?.assignee?.id,
      firstName: weldForeman?.assignee?.firstName,
      lastName: weldForeman?.assignee?.lastName,
      persona: weldForeman?.assignee?.persona,
      imageUrl: weldForeman?.assignee?.imageUrl,
      badgeNumber: weldForeman?.assignee?.badgeNumber,
    };
    initialValues.fieldEngineer = {
      __typename: 'CrewMember',
      id: weldFieldEngineer?.assignee?.id,
      firstName: weldFieldEngineer?.assignee?.firstName,
      lastName: weldFieldEngineer?.assignee?.lastName,
      persona: weldFieldEngineer?.assignee?.persona,
      imageUrl: weldFieldEngineer?.assignee?.imageUrl,
      badgeNumber: weldFieldEngineer?.assignee?.badgeNumber,
    };
  }, [commodityAssignees, isFitupMilestone]);

  const weldFWEAssignee = find(commodityAssignees, (weldAssignee) => {
    return weldAssignee.personaType === PersonaType.FIELD_WELD_ENGINEER;
  })?.assignee;
  const weldFMAssignee = find(commodityAssignees, (weldAssignee) => {
    return weldAssignee.personaType === PersonaType.FOREMAN;
  })?.assignee;
  const [
    getFWEList,
    { data: fweData, error: fweError, loading: fweLoading, refetch: fweRefetch, networkStatus: fweNetworkStatus },
  ] = useCrewMember();
  useEffect(() => {
    if (!canAssignFieldWeldEngineer) return;
    getFWEList({
      variables: {
        input: {
          persona: PersonaType.FIELD_WELD_ENGINEER,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [getFWEList, canAssignFieldWeldEngineer]);

  const fweList = get(fweData, 'crewMembers.items', []) as CrewMemberList['items'];

  const [
    assignCommodityCrewMembers,
    {
      loading: assignCommodityCrewMemberLoading,
      error: assignCommodityCrewMemberError,
      data: assignCommodityCrewMemberData,
    },
  ] = useAssignCommodityCrewMembers(
    () => {
      setActionCompleted(true);
    },
    openFromWeldModule ? ['commodities'] : []
  );

  useEffect(() => {
    if (!actionCompleted) return;
    const erroredCommodityAssignments =
      get(assignCommodityCrewMemberData, 'assignCommodityCrewMembers.erroredCommodityAssignments') ||
      assignCommodityCrewMemberError;
    if (erroredCommodityAssignments && erroredCommodityAssignments?.length == 0) {
      setAssignCommodityCrewMembersErrors(null);
      const assignCommodityCrewMemberToast: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.SUCCESS,
          content: `${t`Crew members assigned successfully.`}`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(assignCommodityCrewMemberToast);
      onClose && onClose();
    } else {
      setAssignCommodityCrewMembersErrors(erroredCommodityAssignments);
    }
    setActionCompleted(false);
  }, [actionCompleted]);

  const closeModal = () => {
    setAssignCommodityCrewMembersErrors(null);
  };

  const onSearchFWE = (searchText: string) => {
    if (searchText.length > 0 && searchText.length < 3) return;
    getFWEList({
      variables: {
        input: {
          searchKeyword: searchText,
          persona: PersonaType.FIELD_WELD_ENGINEER,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={async (values) => {
          const assignCommodityInput: AssignCommodityInput[] = [];
          if (
            canAssignFieldWeldEngineer &&
            values?.fieldEngineer?.id &&
            values?.fieldEngineer?.id !== initialValues?.fieldEngineer?.id
          ) {
            assignCommodityInput.push({
              commodityId: weld.id,
              crewMemberId: values?.fieldEngineer?.id ? values?.fieldEngineer?.id : null,
              personaType: PersonaType.FIELD_WELD_ENGINEER,
            });
          }
          if (canAssignForeman && values?.foreman?.id && initialValues?.foreman?.id !== values?.foreman?.id) {
            assignCommodityInput.push({
              commodityId: weld.id,
              crewMemberId: values?.foreman?.id ? values?.foreman?.id : null,
              personaType: PersonaType.FOREMAN,
            });
          }

          const input: AssignCommoditiesInput = {
            input: assignCommodityInput,
          };
          assignCommodityCrewMembers({
            variables: { input },
          });
        }}
      >
        {(props: FormikProps<FormikState>) => {
          const { handleSubmit, dirty, isValid } = props;
          return (
            <Form data-testid="weld-assign-owner">
              <SideSheet
                visible
                onScroll={(e) => {
                  e.stopPropagation();
                }}
              >
                <SideSheetHeader title={t`Edit Assign`}>
                  <SideSheetClose onClick={onClose}></SideSheetClose>
                </SideSheetHeader>
                <SideSheetSubHeaderStyled>
                  <Box alignItems={{ sm: 'center' }} mb={{ sm: '400' }}>
                    <TextWithEllipsis
                      data-testid="weld-tag"
                      family="graphik"
                      size="500"
                      weight="600"
                      transform="uppercase"
                    >
                      {weld?.tag}
                    </TextWithEllipsis>
                  </Box>
                  <Box mb={{ sm: '400' }}>
                    {weld?.pipeMaterial && (
                      <Box data-testid="weld-pipe-material" mr={{ sm: '400' }}>
                        <Text size="300" color="grey.700">
                          {weld?.pipeMaterial}
                        </Text>
                      </Box>
                    )}
                    {weld?.pipeMaterial && !!weld?.pipeDiameter && <EllipseStyled data-testid="weld-ellipsis" />}
                    {!!weld?.pipeDiameter && (
                      <Text data-testid="weld-pipe-diameter" size="300" color="grey.700">
                        {`${weld?.pipeDiameter}” ${t`Diameter`}`}
                      </Text>
                    )}
                  </Box>
                </SideSheetSubHeaderStyled>
                <SideSheetBody>
                  <SideSheetContentCol>
                    {canAssignForeman &&
                      weld?.status?.status !== CommodityStatusCode.CLAIMED &&
                      weld?.status?.status !== CommodityStatusCode.VT_APPROVED && (
                        <>
                          {canAssignWeldForeman && !initialValues?.foreman?.id ? (
                            <ForemanList />
                          ) : (
                            canReassignWeldForeman && initialValues?.foreman?.id && <ForemanList />
                          )}
                        </>
                      )}
                    {canAssignFieldWeldEngineer && weld?.status?.status !== CommodityStatusCode.VT_APPROVED && (
                      <>
                        <BlockHeadingStyled>
                          <Trans>Select FWE</Trans>
                        </BlockHeadingStyled>
                        {!fweLoading && fweError && (
                          <div data-testid="welder-error">
                            <ErrorRetry refetch={fweRefetch}>
                              <Trans>There was a problem loading Field Weld Engineers</Trans>
                            </ErrorRetry>
                          </div>
                        )}
                        {!fweError && (
                          <WeldPersonaSelector
                            name="fieldEngineer"
                            items={fweList}
                            placeHolderLabel={t`Search FWE`}
                            loading={fweLoading || fweNetworkStatus === NetworkStatus.refetch}
                            onSearch={onSearchFWE}
                          />
                        )}
                      </>
                    )}
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        disabled={assignCommodityCrewMemberLoading}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={onClose}
                      >
                        <Trans>Cancel</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                    <Box>
                      <DrawerFooterButtonStyled
                        onClick={() => handleSubmit()}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={assignCommodityCrewMemberLoading || !dirty || !isValid}
                      >
                        {assignCommodityCrewMemberLoading && <Spinner />}
                        {!assignCommodityCrewMemberLoading &&
                          ((weldFMAssignee && weldFWEAssignee) ||
                          (weldFMAssignee && (canReassignWeldForeman || canAssignWeldForeman)) ||
                          (weldFWEAssignee && canAssignFieldWeldEngineer)
                            ? t`Reassign`
                            : t`Assign`)}
                      </DrawerFooterButtonStyled>
                    </Box>
                  </Box>
                </SideSheetFooter>
              </SideSheet>
            </Form>
          );
        }}
      </Formik>
      {(assignCommodityCrewMembersErrors instanceof ApolloError ||
        (assignCommodityCrewMembersErrors instanceof Array && assignCommodityCrewMembersErrors?.length > 0)) && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <AssignCommodityCrewMembersErrorModal
              commodityAssignmentErrors={assignCommodityCrewMembersErrors}
              close={closeModal}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
